import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { C, EASE_OUT, hover } from '../kit';
import { buildSteps, ComicPage, ReaderMode, stepForPage } from './reader';

/**
 * The comic on the page: a fanned stack of pages. Clicking any of them opens a reader in a
 * modal at that page, with buttons, arrow keys and swipes to turn pages.
 */

const useMode = (): ReaderMode => {
  const query = '(max-width: 760px)';
  const get = () => (typeof window !== 'undefined' && window.matchMedia?.(query).matches ? 'narrow' : 'wide');
  const [mode, setMode] = useState<ReaderMode>(get);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setMode(mq.matches ? 'narrow' : 'wide');
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return mode;
};

export const ComicShelf: React.FC<{ pages: ComicPage[]; preview: number[]; pdf?: string }> = ({ pages, preview, pdf }) => {
  const [openAt, setOpenAt] = useState<number | null>(null);
  return (
    <>
      <Shelf>
        {preview.map((page, i) => (
          <button
            key={pages[page].src}
            type="button"
            className={`p p${i}`}
            aria-label="Open the comic"
            onClick={() => setOpenAt(page)}
          >
            <img src={pages[page].src} alt="" loading="lazy" />
          </button>
        ))}
      </Shelf>
      <Actions>
        <ReadHint type="button" onClick={() => setOpenAt(0)}>
          Read the comic
        </ReadHint>
        {pdf && (
          <a className="dl" href={pdf} download="pepes-dog-comic.pdf">
            <DownloadIcon />
            Download the comic (PDF)
          </a>
        )}
      </Actions>
      {openAt !== null && <ComicReader pages={pages} startPage={0} pdf={pdf} onClose={() => setOpenAt(null)} />}
    </>
  );
};

export const ComicReader: React.FC<{ pages: ComicPage[]; startPage: number; pdf?: string; onClose: () => void }> = ({
  pages,
  startPage,
  pdf,
  onClose,
}) => {
  const mode = useMode();
  const steps = useMemo(() => buildSteps(pages, mode), [pages, mode]);
  const [step, setStep] = useState(() => stepForPage(steps, startPage));
  const [dir, setDir] = useState<1 | -1>(1);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touch = useRef<number | null>(null);

  // keep the same page when the layout switches between wide and narrow
  const pageRef = useRef(startPage);
  useEffect(() => {
    setStep(stepForPage(steps, pageRef.current));
  }, [steps]);
  useEffect(() => {
    pageRef.current = steps[step]?.page ?? 0;
  }, [step, steps]);

  const go = useCallback(
    (delta: 1 | -1) => {
      setDir(delta);
      setStep((s) => Math.min(steps.length - 1, Math.max(0, s + delta)));
    },
    [steps.length]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const prevFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [go, onClose]);

  // preload the neighbours so turning a page is instant
  useEffect(() => {
    [steps[step + 1], steps[step - 1]].forEach((s) => {
      if (s) new Image().src = s.src;
    });
  }, [step, steps]);

  const current = steps[step];
  const atStart = step === 0;
  const atEnd = step === steps.length - 1;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Pepe's Dog comic reader"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchStart={(e) => {
        touch.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touch.current === null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        touch.current = null;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}
    >
      <button ref={closeRef} type="button" className="close" aria-label="Close the comic" onClick={onClose}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 5 L19 19 M19 5 L5 19" />
        </svg>
      </button>

      <Stage>
        <Page key={`${current.src}-${current.half}`} $dir={dir}>
          <Frame $ratio={current.half || !pages[current.page].spread ? 945 / 1300 : 1890 / 1300} $half={current.half}>
            <img data-testid="comic-page" src={current.src} alt={`Comic page ${current.page + 1} of ${pages.length}`} />
          </Frame>
        </Page>
      </Stage>

      <Bar>
        <button type="button" className="nav" aria-label="Previous page" disabled={atStart} onClick={() => go(-1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 4 L7 12 L15 20" />
          </svg>
        </button>
        {pdf && (
          <a className="dl" href={pdf} download="pepes-dog-comic.pdf" aria-label="Download the comic as PDF">
            <DownloadIcon />
          </a>
        )}
        <span className="count" aria-live="polite">
          {current.page + 1} / {pages.length}
        </span>
        <button type="button" className="nav" aria-label="Next page" disabled={atEnd} onClick={() => go(1)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4 L17 12 L9 20" />
          </svg>
        </button>
      </Bar>
    </Overlay>,
    document.body
  );
};

const DownloadIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 L12 15 M6 10 L12 16 L18 10 M4 20 L20 20" />
  </svg>
);

/* ------------------------------------------------------------------- styles */

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 14px 28px;
  margin-top: 26px;
  .dl {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    font-weight: 700;
    color: ${C.ink};
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
  }
  .dl svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: ${C.ink};
    stroke-width: 2.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 200ms ${EASE_OUT};
  }
  ${hover(css`
    .dl:hover svg {
      transform: translateY(3px);
    }
  `)}
`;

const Shelf = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  .p {
    padding: 0;
    background: none;
    border: 0;
    cursor: zoom-in;
    transition: transform 320ms ${EASE_OUT};
  }
  .p img {
    display: block;
    width: auto;
    height: clamp(110px, 21vw, 300px);
    border: 3px solid ${C.ink};
    border-radius: 6px;
  }
  .p0 {
    transform: rotate(-7deg) translateX(24px);
  }
  .p1 {
    position: relative;
    z-index: 1;
    transform: translateY(-10px);
  }
  .p2 {
    transform: rotate(7deg) translateX(-24px);
  }
  .p:focus-visible {
    outline: 3px dashed ${C.ink};
    outline-offset: 6px;
  }
  ${hover(css`
    &:hover .p0 {
      transform: rotate(-12deg) translateX(-6px);
    }
    &:hover .p1 {
      transform: translateY(-22px) scale(1.04);
    }
    &:hover .p2 {
      transform: rotate(12deg) translateX(6px);
    }
    .p:hover {
      z-index: 2;
    }
  `)}
`;

const ReadHint = styled.button`
  display: block;
  min-height: 44px;
  margin: 0;
  padding: 0 26px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 20px;
  color: ${C.ink};
  background: ${C.yellow};
  border: 3.5px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 4px 5px 0 ${C.ink};
  cursor: pointer;
  transition: transform 160ms ${EASE_OUT}, box-shadow 160ms ${EASE_OUT};
  &:active {
    transform: translate(3px, 4px);
    box-shadow: 1px 1px 0 ${C.ink};
  }
  ${hover(css`
    &:hover {
      transform: translate(-1px, -2px) rotate(-1.5deg);
      box-shadow: 6px 8px 0 ${C.ink};
    }
  `)}
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  grid-template-rows: 1fr auto;
  padding: clamp(16px, 3vw, 40px) clamp(8px, 3vw, 40px) 16px;
  padding-top: max(clamp(16px, 3vw, 40px), env(safe-area-inset-top));
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  background: rgba(14, 27, 51, 0.92);
  animation: ${fadeIn} 200ms ${EASE_OUT};
  overscroll-behavior: contain;
  touch-action: pan-y;
  .close {
    position: absolute;
    top: max(14px, env(safe-area-inset-top));
    right: 14px;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    background: ${C.yellow};
    border: 3px solid ${C.ink};
    border-radius: 50%;
    box-shadow: 3px 3px 0 ${C.ink};
    cursor: pointer;
    transition: transform 160ms ${EASE_OUT};
  }
  .close svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: ${C.ink};
    stroke-width: 3.5;
    stroke-linecap: round;
  }
  ${hover(css`
    .close:hover {
      transform: rotate(90deg);
    }
  `)}
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Stage = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  min-height: 0;
  overflow: hidden;
`;

const turnIn = (dir: 1 | -1) => keyframes`
  from { opacity: 0; transform: translateX(${dir * 40}px) rotateY(${dir * -12}deg); }
  to { opacity: 1; transform: none; }
`;

const Page = styled.div<{ $dir: 1 | -1 }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  perspective: 1400px;
  animation: ${(p) => turnIn(p.$dir)} 280ms ${EASE_OUT};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * The largest box with the page's proportions that fits the stage. A half spread is a
 * single-page box showing the left or right half of the double image.
 */
const Frame = styled.div<{ $ratio: number; $half: 'left' | 'right' | null }>`
  position: relative;
  aspect-ratio: ${(p) => p.$ratio};
  width: min(100%, calc((100vh - 150px) * ${(p) => p.$ratio}));
  width: min(100%, calc((100dvh - 150px) * ${(p) => p.$ratio}));
  overflow: hidden;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 6px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
  img {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    height: 100%;
    width: ${(p) => (p.$half ? '200%' : '100%')};
    max-width: none;
    transform: translateX(${(p) => (p.$half === 'right' ? '-50%' : '0')});
    user-select: none;
    -webkit-user-drag: none;
  }
`;

const Bar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding-top: 14px;
  .nav {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    background: ${C.yellow};
    border: 3px solid ${C.ink};
    border-radius: 50%;
    box-shadow: 3px 3px 0 ${C.ink};
    cursor: pointer;
    transition: transform 160ms ${EASE_OUT}, opacity 160ms ${EASE_OUT};
  }
  .nav svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: ${C.ink};
    stroke-width: 3.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .nav:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .nav:not(:disabled):active {
    transform: translate(2px, 2px);
  }
  ${hover(css`
    .nav:not(:disabled):hover {
      transform: scale(1.08);
    }
  `)}
  .dl {
    position: absolute;
    right: clamp(12px, 3vw, 40px);
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    background: ${C.white};
    border: 3px solid ${C.ink};
    border-radius: 50%;
    box-shadow: 3px 3px 0 ${C.ink};
  }
  .dl svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: ${C.ink};
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .count {
    min-width: 70px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 20px;
    color: ${C.white};
    text-align: center;
  }
`;
